import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Divider,
  useTheme,
} from "@mui/material";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import FilterBar from "../../../components/FilterNSearchBar/FilterNSearchBar";
import VendorSummary from "./components/VendorSummary";
import VendorHeader from "./components/VendorHeader";
import VendorTable from "./components/VendorTable";
import VendorEmptyState from "./components/VendorEmptyState";
import AddVendorDialog from "./features/AddVendorDialog";
import useVendorStore from "../store/useVendorStore";
import { styles } from "../styles/Vendors.styles";
import { getOwnerLabel } from "../hooks/useVendorStaff";

const VendorRegisterTab = ({ onGoToDiscovered, people = [] }) => {
  const theme = useTheme();
  const vendors = useVendorStore((s) => s.vendors);
  const archiveVendor = useVendorStore((s) => s.archiveVendor);
  const addVendor = useVendorStore((s) => s.addVendor);

  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);

  const categories = useMemo(
    () =>
      Array.from(new Set(vendors.map((v) => v.category).filter(Boolean))).sort(),
    [vendors]
  );

  const filtered = useMemo(() => {
    return vendors.filter((v) => {
      if (showArchived ? v.status !== "Archived" : v.status === "Archived") return false;
      if (searchQuery) {
        const q = searchQuery.toLowerCase().trim();
        if (
          !v.name.toLowerCase().includes(q) &&
          !(v.services || "").toLowerCase().includes(q) &&
          !getOwnerLabel(people, v.owner).toLowerCase().includes(q) &&
          !(v.owner || "").toLowerCase().includes(q)
        ) {
          return false;
        }
      }
      if (selectedCategory && v.category !== selectedCategory) return false;
      return true;
    });
  }, [vendors, showArchived, searchQuery, selectedCategory, people]);

  const handleArchive = (id) => {
    const vendor = vendors.find((v) => v.id === id);
    archiveVendor(id);
    setSnackbarInfo({
      message:
        vendor?.status === "Archived"
          ? `${vendor?.name} restored`
          : `${vendor?.name} archived`,
    });
  };

  const handleAddVendor = (data) => {
    addVendor(data);
    setAddOpen(false);
    setSnackbarInfo({ message: `${data.name} added to your vendor register` });
  };

  return (
    <>
      <ConfirmSnackbar snackbarInfo={snackbarInfo} />

      <VendorSummary vendors={vendors} />

      <Card sx={styles.mainCard} variant="outlined">
        <CardHeader
          title={
            <VendorHeader
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onAddVendor={() => setAddOpen(true)}
              hasActiveFilters={!!(searchQuery || selectedCategory)}
            />
          }
          sx={styles.cardHeader}
        />
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: 2,
              pb: 1.5,
              backgroundColor: theme.customColors.background.main,
            }}
          >
            <FilterBar
              searchQuery={searchQuery}
              onSearchChange={(e) => setSearchQuery(e.target.value)}
              filterLabel="Filter by Category"
              filters={categories}
              selectedFilter={selectedCategory}
              onFilterChange={setSelectedCategory}
              onClearFilters={() => {
                setSearchQuery("");
                setSelectedCategory(null);
              }}
              countLabel={`${filtered.length} vendor${filtered.length !== 1 ? "s" : ""} found`}
            />
          </Box>

          <Divider sx={{ borderColor: theme.customColors.border.main }} />

          {filtered.length > 0 ? (
            <VendorTable vendors={filtered} onArchive={handleArchive} people={people} />
          ) : (
            <VendorEmptyState
              onAddVendor={() => setAddOpen(true)}
              archived={showArchived}
            />
          )}
        </CardContent>
      </Card>

      <AddVendorDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleAddVendor}
        people={people}
        onGoToDiscovered={() => {
          setAddOpen(false);
          onGoToDiscovered?.();
        }}
      />
    </>
  );
};

export default VendorRegisterTab;
